import { isTauri } from './platform';
import { activeSpace } from './spaces';

/**
 * The tenant (app instance) this client talks to — the single place that
 * knows it.
 *
 * In the browser the server resolves the tenant from the page origin and
 * nothing here changes behavior. The native app's WebView origin
 * (`tauri://localhost`, `http://tauri.localhost`) carries no tenant, so the
 * app names its domain explicitly: as the `X-Andor-Domain` header on HTTP
 * requests and the `domain` parameter on the notifications WebSocket.
 * The domain is the space chosen in the app (see `spaces.ts`); switching
 * reloads the page, so it is fixed for a page's lifetime.
 */
export const TENANT_HEADER = 'X-Andor-Domain';

const nativeSpace = isTauri ? activeSpace() : null;

/** The native app's tenant domain; `null` in the browser and before a
 * space was added in the app. */
export function nativeTenantDomain(): string | null {
  return nativeSpace?.domain ?? null;
}

/** Public origin of the tenant, used for links people open elsewhere. A
 * local space opens in a browser on the development machine, under its own
 * name on the server it is reached through (`http://foo.localhost:4200`). */
export function tenantOrigin(): string {
  if (!nativeSpace) return location.origin;
  if (!nativeSpace.server) return `https://${nativeSpace.domain}`;
  const url = new URL(nativeSpace.server);
  url.hostname = nativeSpace.domain;
  return url.origin;
}

/** Headers naming the tenant; empty in the browser. */
export function tenantHeaders(): Record<string, string> {
  const domain = nativeTenantDomain();
  return domain ? { [TENANT_HEADER]: domain } : {};
}

/**
 * Storage key scoped to the tenant. The browser already scopes storage by
 * origin, so keys stay unchanged there; every native tenant shares the
 * WebView origin and gets its own prefix.
 */
export function tenantStorageKey(key: string): string {
  const domain = nativeTenantDomain();
  return domain ? `${domain}:${key}` : key;
}
